/**
 * Route-level containers.
 *
 * The screens in this folder are presentational on purpose — they take data and
 * callbacks, which is what makes them testable without a network. These containers
 * supply them: one per route, holding the queries, mutations and the lesson state
 * machine, so no view component ever reaches for the API itself.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clientUlid } from '@vibelang/api-client';
import {
  levelFromProbe,
  PERSONA_AUDIO_KEYS,
  PREVIEW_AUDIO_KEYS,
  probeFor,
  scoreProbe,
} from '@vibelang/content';
import type { RouteResponse } from '@vibelang/contracts';
import {
  CEFR_SUBLEVELS,
  type Dialect,
  type DisputeReason,
  initialContext,
  type Language,
  type LessonContext,
  type LessonEvent,
  transitionWithDiagnostics,
  vocabToIndex,
} from '@vibelang/core';
import { EmptyState, ErrorState, Skeleton } from '@vibelang/ui';
import type { JSX } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi, useApp, useCourseId } from '../app-context.js';
import { Recorder, uploadContentType } from '../lib/audio.js';
import { measuredScores, mediaUrl, qk, retryIf } from '../lib/client.js';
import { LessonSession, ReportCardScreen } from './lesson.js';
import { ConversationReportScreen } from './live.js';
import {
  emptyDraft,
  type FirstScore,
  firstSentence,
  Onboarding,
  type OnboardingDraft,
  type OnboardingStep,
} from './onboarding.js';
import { type PendingGrade, ReviewSession } from './review.js';
import { CoachmarkTour, StreakDetailSheet, TodayScreen, UsageSheet } from './today.js';

export interface NavigationProps {
  readonly onNavigate: (route: string) => void;
}

// ── Today ───────────────────────────────────────────────────────────────────

export function TodayRoute({ onNavigate }: NavigationProps): JSX.Element {
  const api = useApi();
  const { courseId, offline, requestPaywall } = useApp();
  /**
   * Which sheet Today has open.
   *
   * The meter and the streak used to navigate to Settings and to Progress. Both
   * sheets existed, were written, and were mounted nowhere — so tapping the meter
   * took the learner to a page that does not explain the meter, and three events
   * the specs name had no source.
   */
  const [sheet, setSheet] = useState<'usage' | 'streak' | null>(null);
  const [tourDone, setTourDone] = useState(() => readTourSeen());

  const today = useQuery<RouteResponse<'getToday'>>({
    queryKey: qk.today(courseId ?? ''),
    queryFn: () => api.today(courseId ?? '') as Promise<RouteResponse<'getToday'>>,
    enabled: courseId !== null && sheet !== null,
  });

  if (courseId === null) {
    // No course means onboarding has not finished, whatever a local flag says.
    // Trusting the flag is how someone ends up on a permanently empty Today.
    return <OnboardingRoute onNavigate={onNavigate} />;
  }

  const loaded = today.data;

  return (
    <>
      <TodayScreen
        courseId={courseId}
        onNavigate={onNavigate}
        onOpenUsage={() => setSheet('usage')}
        onOpenStreak={() => setSheet('streak')}
        offline={offline}
      />

      {/* The tour runs once, on the first open, and marks itself seen whichever way
          it ends — finishing and skipping are both "do not show this again". */}
      {!tourDone && (
        <CoachmarkTour
          onFinish={() => {
            writeTourSeen();
            setTourDone(true);
          }}
        />
      )}

      {sheet === 'usage' && loaded !== undefined && (
        <UsageSheet
          used={loaded.usage.voiceSecondsUsed}
          total={loaded.usage.voiceSecondsTotal}
          resetsAt={loaded.usage.resetsAt ?? 0}
          state={loaded.usage.state}
          trigger="meter_tap"
          onUpgrade={() => {
            setSheet(null);
            requestPaywall('voice_limit');
          }}
          onNavigate={(route) => {
            setSheet(null);
            onNavigate(route);
          }}
        />
      )}

      {sheet === 'streak' && loaded !== undefined && (
        <StreakDetailSheet
          streak={loaded.streak}
          onShare={() => undefined}
          onNavigate={(route) => {
            setSheet(null);
            onNavigate(route);
          }}
        />
      )}
    </>
  );
}

const TOUR_KEY = 'vl.tour.seen';

/**
 * Whether the coachmark tour has run. Per browser, and a storage failure reads as
 * "seen" — showing a tour twice is worse than never showing it.
 */
function readTourSeen(): boolean {
  try {
    return globalThis.localStorage?.getItem(TOUR_KEY) !== null;
  } catch {
    return true;
  }
}

function writeTourSeen(): void {
  try {
    globalThis.localStorage?.setItem(TOUR_KEY, String(Date.now()));
  } catch {
    // Storage unavailable. The tour runs again next time, which is the harmless way
    // for this to fail.
  }
}

// ── Onboarding ──────────────────────────────────────────────────────────────

export function OnboardingRoute({ onNavigate }: NavigationProps): JSX.Element {
  const api = useApi();
  const { setCourseId, setInOnboarding, track } = useApp();
  const [draft, setDraft] = useState<OnboardingDraft>(emptyDraft());
  const [step, setStep] = useState<OnboardingStep>('language');

  // The probe actually decides the starting level, rather than being collected and
  // discarded. Null until the learner has answered, so an untouched probe makes no
  // claim at all.
  const estimatedLevel =
    draft.knownWordIds.length === 0
      ? null
      : levelFromProbe(
          scoreProbe(draft.language ?? 'es', draft.knownWordIds),
          vocabToIndex,
          CEFR_SUBLEVELS,
        );

  const create = useMutation({
    mutationFn: () =>
      api.call('createCourse', {
        body: {
          // Client-generated, so a retry after a timeout returns the same course
          // rather than silently creating a second one.
          id: clientUlid(),
          language: draft.language ?? 'es',
          dialect: draft.dialect ?? 'es-MX',
          goals: draft.goals.slice(0, 2) as (
            | 'travel'
            | 'work'
            | 'family'
            | 'exam'
            | 'moving'
            | 'curiosity'
          )[],
          dailyGoalMinutes: draft.dailyGoalMinutes,
          personaId: draft.personaId as 'mika' | 'dani' | 'sol' | 'rex' | 'vex' | 'nakamura',
          personaStrictness: draft.strictness,
          correctionStyle: draft.correctionStyle,
          // Sent, not just shown. Asking a learner their level and then starting them
          // on greetings anyway is the closest competitor's most-repeated complaint
          // (docs/research/03-competitor-pingo.md §4). Honoured at low confidence
          // server-side, so their own speech corrects it within a session or two.
          ...(estimatedLevel === null ? {} : { selfDeclaredLevel: estimatedLevel }),
          // Translations available on request rather than always on: a visible
          // translation removes the work that makes the practice worth doing.
          scaffolding: 'l1_supported',
          reminderLocalTime: draft.reminderLocalTime,
        },
      }),
    onSuccess: (course) => {
      setCourseId(course.id);
      setInOnboarding(false);
      onNavigate('/');
    },
  });

  return (
    <Onboarding
      draft={draft}
      onChange={setDraft}
      step={step}
      onStep={setStep}
      track={track}
      // The scored utterance happens at step three, before any price is shown.
      onRecordFirstUtterance={() =>
        recordAndScoreFirstUtterance(api, draft.language ?? 'es', draft.dialect ?? 'es-MX')
      }
      // Null on any failure, and the plan screen simply omits those lines: a count we
      // could not fetch must not become a number we made up.
      onFetchCatalogue={async (dialect) => {
        try {
          const result = await api.call('courseCatalogue', { query: { dialect } });
          return { lessons: result.lessons, scenarios: result.scenarios, items: result.items };
        } catch {
          return null;
        }
      }}
      onRequestMic={async () => {
        const recorder = new Recorder();
        try {
          await recorder.open();
          return true;
        } catch {
          return false;
        } finally {
          await recorder.close();
        }
      }}
      onRequestNotifications={async () => {
        if (!('Notification' in globalThis)) return false;
        return (await Notification.requestPermission()) === 'granted';
      }}
      /**
       * The dialect "Hear it" button, which until now rendered and did nothing.
       *
       * Its own comment calls it "the only way a learner who has never heard the
       * difference can actually choose", and dialect is the product's differentiator, so
       * a dead button there was the worst placed one in the app. The recording is the
       * same sentence in every dialect of a language and is public content audio, so it
       * plays with no course and no token.
       */
      onPreviewDialect={(dialect) => {
        const url = mediaUrl(`/v1/audio/${encodeURIComponent(PREVIEW_AUDIO_KEYS[dialect])}`);
        if (url === null) return;
        void new Audio(url).play().catch(() => {
          // Autoplay policy can refuse the first gesture; the button is still there.
        });
      }}
      /** The tutor sample, which is the dialect preview's counterpart one step later. */
      onPreviewPersona={(personaId) => {
        const key = PERSONA_AUDIO_KEYS[personaId];
        if (key === undefined) return;
        const url = mediaUrl(`/v1/audio/${encodeURIComponent(key)}`);
        if (url === null) return;
        void new Audio(url).play().catch(() => {
          // Autoplay policy can refuse the first gesture; the button is still there.
        });
      }}
      onFinish={() => create.mutate()}
      placementWords={probeFor(draft.language ?? 'es')}
      estimatedLevel={estimatedLevel}
    />
  );
}

/**
 * Records the learner's first utterance and scores it for real.
 *
 * Extracted and exported because of what it replaced: a hard-coded `overall: 72`,
 * shown on the one screen this whole product is a wedge for. A fabricated score is the
 * single thing we may never put in front of a learner, so the honest path is the one
 * with a test on it.
 *
 * Every failure here resolves to a *withheld* score, never a number and never a zero.
 */
export async function recordAndScoreFirstUtterance(
  api: ReturnType<typeof useApi>,
  language: Language,
  dialect: Dialect,
  recorder: Recorder = new Recorder(),
): Promise<FirstScore> {
  const withheld = (withheldMessage: string): FirstScore => ({
    overall: null,
    band: null,
    note: null,
    withheld: true,
    withheldMessage,
  });

  try {
    await recorder.open();
    recorder.start();
    const result = await recorder.stop();

    // Too little speech to judge never reaches the scorer: withheld locally, which is
    // both honest and free.
    if (result.signal.speechRatio < 0.15) {
      return withheld("We didn't catch enough to score that — try once more.");
    }

    const contentType = uploadContentType(result.mimeType);
    const upload = await api.call('createAudioUpload', {
      body: {
        sessionId: null,
        contentType,
        bytes: result.blob.size,
        durationMs: result.durationMs,
      },
    });
    await fetch(upload.uploadUrl, {
      method: 'PUT',
      headers: { 'content-type': contentType },
      body: result.blob,
    });

    const feedback = await api.call('scoreFirstUtterance', {
      body: {
        id: clientUlid(),
        audioKey: upload.audioKey,
        // The same sentence the screen displayed. Scoring against a different one
        // would mark a correct utterance wrong.
        expectedText: firstSentence(language),
        dialect,
        durationMs: result.durationMs,
        signal: result.signal,
      },
    });

    // A score we could not get is withheld, not zero.
    if (feedback.status !== 'scored' || feedback.overall === null) {
      return withheld(
        feedback.message ?? "We couldn't score that just now. Your setup carries on either way.",
      );
    }

    return {
      overall: feedback.overall,
      band: feedback.band?.id ?? null,
      // The scorer's own sentence, or none. A note we assembled here is a claim
      // nothing stands behind.
      note: feedback.message,
      withheld: false,
      withheldMessage: null,
    };
  } catch {
    return withheld("We couldn't score that just now. Your setup carries on either way.");
  } finally {
    await recorder.close();
  }
}

// ── Lesson session ──────────────────────────────────────────────────────────

/**
 * Drives one lesson through the shared state machine.
 *
 * Every transition goes through `transitionWithDiagnostics`, and an unhandled pair is
 * reported rather than swallowed — a silent no-op is exactly how a lesson gets stuck,
 * which is the competitor failure this machine exists to prevent.
 */
export function LessonSessionRoute({
  lessonId,
  onNavigate,
}: NavigationProps & { readonly lessonId: string }): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const { setInActiveSession, track } = useApp();

  const recorder = useRef<Recorder | null>(null);
  // The session's own clock and counters, in refs: a re-render must not reset them, and
  // the completion event is the one that pays for all the others.
  const turnStartedAt = useRef<number | null>(null);
  const hintsUsed = useRef(0);
  const retries = useRef(0);
  const started = useRef(false);
  const completed = useRef(false);
  const [context, setContext] = useState<LessonContext>(() => initialContext('pending', 0));
  const [micState, setMicState] = useState<LessonSessionMic>('idle');
  const [level, setLevel] = useState(0);
  const [translationVisible, setTranslationVisible] = useState(false);
  const [degradeReason, setDegradeReason] = useState<string | null>(null);

  /**
   * The turn on screen, and the one after it.
   *
   * This whole route used to pass `currentTurn={null}` and never call the server: no
   * session was created, no turn was submitted, nothing was scored. A web lesson was a
   * mic button over a progress line — no tutor line, no transcript, no score, and so no
   * feedback card, which is where the appeal control and the phoneme breakdown live.
   * The screens were all built; nothing was driving them.
   */
  const [turn, setTurn] = useState<LessonTurnView | null>(null);
  const [queued, setQueued] = useState<LessonTurnView | null>(null);
  const [appealOutcome, setAppealOutcome] = useState<string | null>(null);

  // Generated once per mount and reused across retries, so a failed start does not
  // strand a half-created session behind an id nobody holds.
  const sessionId = useRef(clientUlid());

  const lesson = useQuery({
    queryKey: qk.lesson(lessonId),
    queryFn: () => api.call('getLesson', { params: { lessonId } }),
  });

  // `send` is memoised with no dependencies so the state machine cannot be rebuilt
  // mid-session; the tracker is reached through a ref rather than widening that.
  const trackRef = useRef(track);
  trackRef.current = track;

  const send = useCallback((event: LessonEvent) => {
    setContext((current) => {
      const result = transitionWithDiagnostics(current, event);
      for (const effect of result.effects) {
        if (effect.type === 'notifyDegraded') {
          setDegradeReason(
            "The mic dropped out, so we've switched to typing. Your progress is kept.",
          );
          trackRef.current?.('lesson_degraded', { reason: 'audio_session' });
        }
      }
      return result.context;
    });
  }, []);

  /**
   * Starts the session on the server, which is what makes the lesson real.
   *
   * The id is client-generated and sent with the call, so a retried start resumes the
   * same session rather than opening a second one — and the machine is seeded with the
   * server's own turn count rather than with a module total that happened to be nearby.
   */
  const start = useMutation({
    mutationFn: () =>
      api.call('createSession', {
        body: {
          id: sessionId.current,
          courseId,
          kind: 'lesson' as const,
          refId: lessonId,
          mode: 'voice' as const,
        },
      }),
    onSuccess: (created) => {
      setContext(initialContext(created.session.id, created.session.totalTurns));
      setTurn(created.firstTurn);
    },
  });

  const startSession = start.mutate;
  useEffect(() => {
    startSession();
  }, [startSession]);

  /**
   * A lesson pauses when the tab goes away, and resumes when it comes back.
   *
   * `PauseReason` has named `backgrounded` since the machine was written, the `paused`
   * state has a transition in and out of it, and **nothing on any platform ever emitted
   * `PAUSE`** — so the state was unreachable everywhere, iOS had a `pause(reason:)`
   * helper with no caller, and the Android lesson screen rendered a whole "Paused" empty
   * state with a Resume button for a state it could not be in.
   *
   * It matters beyond tidiness: a lesson that stays `listening` while the tab is hidden
   * holds the microphone open and keeps a turn's clock running against a learner who is
   * not there.
   */
  useEffect(() => {
    const onVisibility = () => {
      send(
        document.visibilityState === 'hidden'
          ? { type: 'PAUSE', reason: 'backgrounded' }
          : { type: 'RESUME' },
      );
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [send]);

  /**
   * Submits one turn and takes what comes back.
   *
   * The response carries the score *and* the next turn, so one call advances the
   * conversation and feeds the feedback card. A scoring failure still advances: the
   * machine is total over all three outcomes, and that invariant is the point.
   */
  const submit = useMutation({
    mutationFn: (body: SubmitTurnBody) =>
      api.call('submitTurn', {
        params: { sessionId: sessionId.current, index: context.turnIndex },
        body,
      }),
    onSuccess: (result) => {
      setTurn(result.turn);
      setQueued(result.nextTurn);
      setMicState('idle');
      recordTurn(
        result.scoreStatus === 'pending' ? 'unavailable' : result.scoreStatus,
        result.turn.feedback?.overall ?? null,
      );
      // A withheld score is a completed turn. The learner moves on either way.
      send(
        result.scoreStatus === 'scored' || result.scoreStatus === 'reduced'
          ? { type: 'SCORED' }
          : { type: 'SCORE_FAILED', reason: 'unknown' },
      );
    },
    onError: () => {
      // Our problem, not the learner's: the turn records `unavailable` and the lesson
      // carries on rather than stranding them on a dead screen.
      setMicState('idle');
      recordTurn('unavailable', null);
      send({ type: 'SCORE_FAILED', reason: 'network' });
    },
  });

  /** Files an appeal against the score the learner just saw. */
  const appeal = useMutation({
    mutationFn: (input: { utteranceId: string; consentToStore: boolean }) =>
      api.call('appealScore', {
        body: { utteranceId: input.utteranceId, consentToStore: input.consentToStore },
      }),
    // The appeal is filed either way; only storage depends on consent. Its own message
    // is shown rather than one we compose, because it states what actually happened.
    onSuccess: (result) => setAppealOutcome(result.message),
    onError: () =>
      setAppealOutcome("We couldn't send that just now — we'll try again when you're back online."),
  });

  /**
   * Reports the feedback itself as wrong, which is a different claim from appealing a
   * score: one says "I said it right", the other says "this explanation is nonsense".
   * The competitor's top review theme is bad feedback with nowhere to say so.
   */
  const report = useMutation({
    mutationFn: (utteranceId: string | null) =>
      api.call('submitFeedback', {
        body: {
          id: clientUlid(),
          kind: 'bad_scoring' as const,
          detail: 'Reported from the lesson feedback card.',
          sessionId: sessionId.current,
          utteranceId,
          contentId: lessonId,
          includeDiagnostics: false,
        },
      }),
  });

  /**
   * One turn's outcome. `confidence` carries the score status rather than a number,
   * because "we declined to score this" has to be distinguishable in the data from a
   * low score — they are different events for the learner and for us.
   */
  const recordTurn = (
    confidence: 'scored' | 'reduced' | 'withheld' | 'unavailable',
    score: number | null,
  ) => {
    const began = turnStartedAt.current;
    turnStartedAt.current = null;
    track('lesson_turn_complete', {
      turn: context.turnIndex + 1,
      score,
      confidence,
      hint_used: hintsUsed.current > 0,
      translate_used: translationVisible,
      retries: retries.current,
      latency_ms: began === null ? 0 : Date.now() - began,
    });
    if (began !== null) {
      // Billed seconds, from the learner's side of the meter. The server meters
      // authoritatively; this is what makes a disagreement visible.
      track('voice_minutes_consumed', { seconds: Math.round((Date.now() - began) / 1000) });
    }
  };

  if (lesson.isPending || start.isPending) {
    return <Skeleton lines={6} label="Getting the lesson ready" />;
  }
  if (lesson.error !== null || lesson.data === undefined) {
    return (
      <ErrorState
        title="We couldn't load this lesson"
        {...retryIf(lesson.error, () => void lesson.refetch())}
        alternative={{ label: 'Back to your path', onClick: () => onNavigate('/learn/path') }}
      />
    );
  }
  if (start.error !== null) {
    return (
      <ErrorState
        title="We couldn't start this lesson"
        detail="Nothing was used up."
        {...retryIf(start.error, () => start.mutate())}
        alternative={{ label: 'Back to your path', onClick: () => onNavigate('/learn/path') }}
      />
    );
  }

  const summary = lesson.data.lesson;

  if (!started.current) {
    started.current = true;
    track('lesson_intro_view', { id: lessonId, cefr: summary.cefr ?? null });
    track('lesson_start', {
      id: lessonId,
      mode: context.mode === 'text' ? 'text' : 'voice',
      scaffolding: 'l1_supported',
      persona: 'mika',
    });
  }

  /** Sends the learner to the report, which is where a finished lesson belongs. */
  const finish = () => {
    if (!completed.current) {
      completed.current = true;
      void api.call('completeSession', {
        params: { sessionId: sessionId.current },
        body: {},
      });
    }
    setInActiveSession(false);
    onNavigate(`/learn/lesson/${lessonId}/report/${sessionId.current}`);
  };

  return (
    <LessonSession
      track={track}
      lessonTitle={summary.title}
      lessonGoal={summary.goal}
      lang={courseId.length > 0 ? 'es' : 'es'}
      // The machine's own answer, not an inference from whether a score arrived.
      turnAnswered={context.state === 'turnFeedback'}
      turnIndex={context.turnIndex}
      // The session's own turn count, not the module's. `totalInModule` counts lessons
      // in a module, so "Turn 1 of 12" was counting the wrong thing entirely.
      totalTurns={context.totalTurns}
      currentTurn={turn}
      mode={context.mode === 'text' ? 'text' : 'voice'}
      degradeReason={degradeReason}
      micState={micState}
      micMode="toggle"
      level={level}
      busy={submit.isPending}
      onStartRecording={() => {
        setInActiveSession(true);
        turnStartedAt.current = Date.now();
        const instance = new Recorder({ onLevel: setLevel });
        recorder.current = instance;
        void instance
          .open()
          .then(() => {
            instance.start();
            setMicState('listening');
            send({ type: 'SPEECH_START' });
          })
          .catch(() => {
            // Reported as a device problem rather than as a failed attempt: scoring a
            // silent buffer is exactly the false negative we refuse to produce.
            setMicState('noPermission');
            send({ type: 'AUDIO_LOST' });
          });
      }}
      onStopRecording={() => {
        setMicState('processing');
        send({ type: 'SPEECH_END' });
        void (async () => {
          try {
            const result = await recorder.current?.stop();
            if (result === undefined) throw new Error('nothing was recorded');

            // Audio goes straight to R2 on a presigned PUT: a multi-megabyte recording
            // never occupies a Worker invocation or meets its body limit.
            const contentType = uploadContentType(result.mimeType);
            const upload = await api.call('createAudioUpload', {
              body: {
                sessionId: sessionId.current,
                contentType,
                bytes: result.blob.size,
                durationMs: result.durationMs,
              },
            });
            await fetch(upload.uploadUrl, {
              method: 'PUT',
              headers: { 'content-type': contentType },
              body: result.blob,
            });

            submit.mutate({
              audioKey: upload.audioKey,
              // The line the learner was asked to say. Scoring against anything else
              // would mark a correct utterance wrong.
              expectedText: turn?.tutorText ?? null,
              durationMs: result.durationMs,
              // Measured on-device, so the server can decide to withhold a score
              // rather than guess at a bad one.
              signal: result.signal,
              hintUsed: hintsUsed.current > 0,
              translateUsed: translationVisible,
            });
          } catch {
            setMicState('idle');
            recordTurn('unavailable', null);
            send({ type: 'SCORE_FAILED', reason: 'unknown' });
          } finally {
            await recorder.current?.close();
          }
        })();
      }}
      onSubmitText={(text) => {
        if (text.trim().length === 0) return;
        send({ type: 'TEXT_SUBMITTED' });
        // Text mode is graded on content, not pronunciation, so there is no utterance
        // and no signal to send.
        submit.mutate({
          text,
          hintUsed: hintsUsed.current > 0,
          translateUsed: translationVisible,
        });
      }}
      onNextTurn={() => {
        setAppealOutcome(null);
        if (queued === null) {
          finish();
          return;
        }
        setTurn(queued);
        setQueued(null);
        send({ type: 'NEXT_TURN' });
      }}
      onHint={() => {
        hintsUsed.current += 1;
        send({ type: 'SILENCE_TIMEOUT' });
      }}
      onTranslate={() => setTranslationVisible((visible) => !visible)}
      onRepeat={() => undefined}
      onSwitchToText={() => send({ type: 'DEGRADE', reason: 'unknown' })}
      onRetryVoice={() => {
        retries.current += 1;
        send({ type: 'AUDIO_RECOVERED' });
      }}
      onExit={() => {
        // Abandoning checkpoints first, so closing never loses the turn.
        send({ type: 'ABANDON' });
        if (!completed.current) {
          completed.current = true;
          // Which turn they left on is the single most useful number about a lesson
          // nobody finishes, and it is only knowable here.
          track('lesson_abandoned', { turn: context.turnIndex + 1, reason: 'user_exit' });
        }
        setInActiveSession(false);
        onNavigate(`/learn/lesson/${lessonId}`);
      }}
      onAppeal={(consentToStore) => {
        const utteranceId = turn?.feedback?.utteranceId;
        if (utteranceId === undefined) return;
        appeal.mutate({ utteranceId, consentToStore });
      }}
      onReportFeedback={() => report.mutate(turn?.feedback?.utteranceId ?? null)}
      // Both open minimal-pair practice for the sound in question: the clinic is where
      // a contrast is actually drilled, and it has existed the whole time.
      onPractisePhoneme={(phoneme) => onNavigate(`/review/clinic/${encodeURIComponent(phoneme)}`)}
      appealOutcome={appealOutcome}
      hint={null}
      translationVisible={translationVisible}
    />
  );
}

/** The turn shape the lesson screen renders, which is the contract's own. */
type LessonTurnView = RouteResponse<'submitTurn'>['turn'];
type SubmitTurnBody = {
  readonly text?: string;
  readonly audioKey?: string;
  readonly expectedText?: string | null;
  readonly durationMs?: number;
  readonly signal?: {
    readonly snrDb: number;
    readonly speechRatio: number;
    readonly clipped: boolean;
  };
  readonly hintUsed: boolean;
  readonly translateUsed: boolean;
};

type LessonSessionMic =
  | 'idle'
  | 'listening'
  | 'processing'
  | 'success'
  | 'lowConfidence'
  | 'disabled'
  | 'noPermission';

// ── Report card ─────────────────────────────────────────────────────────────

export function ReportRoute({
  sessionId,
  onNavigate,
}: NavigationProps & { readonly sessionId: string }): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const queryClient = useQueryClient();
  const { track } = useApp();
  const query = useQuery({
    queryKey: qk.report(sessionId),
    queryFn: () => api.call('getReport', { params: { sessionId } }),
  });

  // Emitted from the report rather than from the session, because the server's own
  // numbers are the ones worth recording: a client-side duration counts the time the
  // tab sat open behind another window.
  const report = query.data;
  useEffect(() => {
    if (report === undefined) return;
    track('lesson_complete', {
      id: report.sessionId,
      // The per-dimension scores, which is what "scores" means on every other screen
      // that shows this report.
      scores: measuredScores(report.dimensions),
      duration_ms: report.durationSeconds * 1000,
      turns: report.turns,
      mistakes: report.mistakesFound,
      // The report does not carry a hint count, and a plausible number here would be
      // indistinguishable from a measured one. Null says we did not measure it.
      hints: null,
    });
  }, [report, track]);

  /**
   * The deck, so a word already in it reads as saved rather than offering to be saved
   * twice.
   *
   * Matched on `display`, not on an item id: a saved record stores what the learner
   * saw, and the contract gives it no item reference. Imperfect — two different items
   * that render identically would collapse — and still better than offering to save the
   * same word every time the report is opened.
   */
  const saved = useQuery({
    queryKey: qk.saved(courseId),
    queryFn: () => api.call('listSaved', { query: { courseId, limit: 200 } }),
  });
  const savedDisplays = new Set(
    (saved.data?.items ?? []).map((item) => item.display.toLowerCase()),
  );

  const saveWord = useMutation({
    mutationFn: (display: string) =>
      api.call('saveItem', {
        body: {
          id: clientUlid(),
          courseId,
          kind: 'word' as const,
          display,
          translation: null,
          sourceRoute: `/learn/lesson/report/${sessionId}`,
          audioKey: null,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.saved(courseId) });
    },
  });

  return (
    <ReportCardScreen
      report={query.data ?? null}
      savedItemIds={
        new Set(
          (query.data?.itemsLearned ?? [])
            .filter((item) => savedDisplays.has(item.display.toLowerCase()))
            .map((item) => item.itemId),
        )
      }
      onSaveWord={(_itemId, display) => {
        track('saved_item_add', { source: 'lesson_report' });
        saveWord.mutate(display);
      }}
      loading={query.isPending}
      // The report's primary action is always the drill, which closes the loop rather
      // than ending on a number.
      onDrill={() => {
        track('report_card_drill_tapped', {});
        onNavigate(
          query.data?.drillId === null || query.data === undefined
            ? '/review'
            : `/learn/drill/${query.data.drillId}`,
        );
      }}
      onTranscript={() => {
        track('report_card_transcript_tapped', {});
        onNavigate(`/me/reports/${sessionId}/transcript`);
      }}
      onDone={() => onNavigate('/')}
    />
  );
}

/**
 * The report at the end of a conversation.
 *
 * Distinct from the lesson report because a conversation is judged against its own
 * success criteria first, which is what the learner was actually trying to do. The
 * screen existed and nothing routed to it, so a finished conversation ended nowhere.
 */
export function ConversationReportRoute({
  sessionId,
  onNavigate,
}: NavigationProps & { readonly sessionId: string }): JSX.Element {
  const api = useApi();
  const query = useQuery({
    queryKey: qk.report(sessionId),
    queryFn: () => api.call('getReport', { params: { sessionId } }),
  });

  return (
    <ConversationReportScreen
      report={query.data ?? null}
      loading={query.isPending}
      onDrill={() =>
        onNavigate(
          query.data?.drillId === null || query.data === undefined
            ? '/review'
            : `/learn/drill/${query.data.drillId}`,
        )
      }
      // The same scenario with a stricter tutor, which is the one retry worth
      // offering: repeating it unchanged teaches nothing new.
      onRetryHarder={() => onNavigate('/talk/scenarios')}
      onPlayImproved={() => undefined}
      onDone={() => onNavigate('/talk')}
    />
  );
}

// ── Review session ──────────────────────────────────────────────────────────

export function ReviewSessionRoute({ onNavigate }: NavigationProps): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const queryClient = useQueryClient();
  const { offline, track } = useApp();
  const [allowSpoken, setAllowSpoken] = useState(true);

  const query = useQuery({
    queryKey: qk.reviewQueue(courseId, allowSpoken),
    queryFn: () => api.call('getReviewQueue', { query: { courseId, limit: 40, allowSpoken } }),
  });

  const submit = useMutation({
    mutationFn: (grades: readonly PendingGrade[]) =>
      api.call('submitReviewGrades', {
        body: {
          courseId,
          // A client-generated batch id is what makes a retry safe: the server reports
          // already-applied grades as duplicates rather than scheduling them twice.
          batchId: clientUlid(),
          grades: grades.map((grade) => ({
            cardId: grade.cardId,
            modality: grade.modality,
            correct: grade.correct,
            elapsedMs: grade.elapsedMs,
            hintUsed: grade.hintUsed,
            ...(grade.pronunciationScore === undefined
              ? {}
              : { pronunciationScore: grade.pronunciationScore }),
            reviewedAt: grade.reviewedAt,
          })),
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.today(courseId) });
      onNavigate('/review');
    },
  });

  const report = useMutation({
    mutationFn: (input: { itemId: string; reason: DisputeReason }) =>
      api.call('reportContent', {
        body: {
          id: clientUlid(),
          itemId: input.itemId,
          courseId,
          reason: input.reason,
          note: null,
        },
      }),
    onSuccess: () => {
      // The queue is rebuilt without the item. The card has already gone from the
      // screen; this is so it has not come back by the next session.
      void queryClient.invalidateQueries({ queryKey: qk.reviewQueue(courseId, allowSpoken) });
    },
  });

  return (
    <ReviewSession
      track={track}
      queue={query.data ?? null}
      loading={query.isPending}
      error={query.error}
      offline={offline}
      allowSpoken={allowSpoken}
      // Grades accumulate locally and submit in one batch, so a blip mid-session
      // cannot strand half a queue — and the batch is idempotent server-side.
      onGrade={() => undefined}
      onFinish={(grades) => submit.mutate(grades)}
      onRetry={() => {
        if (query.error !== null) {
          void query.refetch();
        } else {
          setAllowSpoken(!allowSpoken);
        }
      }}
      onNavigate={onNavigate}
      onReport={(itemId, reason) => report.mutate({ itemId, reason })}
    />
  );
}

// ── A report reopened from history ──────────────────────────────────────────

/**
 * A past report card, opened from the history list.
 *
 * Distinct from `ReportRoute` in one way that matters: it does not emit
 * `lesson_complete`. That event means "a lesson was finished", and reopening a report
 * from history finishes nothing — counting it would inflate the completion figure every
 * time a learner looked back at their own work.
 *
 * The history row linked here before this route existed, so the one thing the tier limit
 * sells — keeping your reports — could not be done with them.
 */
export function PastReportRoute({
  sessionId,
  onNavigate,
}: NavigationProps & { readonly sessionId: string }): JSX.Element {
  const api = useApi();
  const { track } = useApp();
  const query = useQuery({
    queryKey: qk.report(sessionId),
    queryFn: () => api.call('getReport', { params: { sessionId } }),
  });

  if (query.error !== null) {
    return (
      <ErrorState
        title="We couldn't open that report"
        {...retryIf(query.error, () => void query.refetch())}
        alternative={{ label: 'All reports', onClick: () => onNavigate('/me/reports') }}
      />
    );
  }

  return (
    <ReportCardScreen
      report={query.data ?? null}
      loading={query.isPending}
      savedItemIds={new Set()}
      onDrill={() => {
        track('report_card_drill_tapped', {});
        onNavigate(
          query.data?.drillId === null || query.data === undefined
            ? '/review'
            : `/learn/drill/${query.data.drillId}`,
        );
      }}
      onTranscript={() => {
        track('report_card_transcript_tapped', {});
        onNavigate(`/me/reports/${sessionId}/transcript`);
      }}
      onDone={() => onNavigate('/me/reports')}
    />
  );
}

/**
 * What was actually said, turn by turn.
 *
 * The report card has offered `View transcript` from the start and the button went
 * nowhere. It matters more here than the scores do: a learner who disputes a score —
 * the competitor's loudest complaint — needs to see what we thought they said, and a
 * number with no transcript behind it is not something anyone can argue with.
 */
export function SessionTranscriptRoute({
  sessionId,
  onNavigate,
}: NavigationProps & { readonly sessionId: string }): JSX.Element {
  const api = useApi();
  const query = useQuery({
    queryKey: ['session', sessionId],
    queryFn: () => api.call('getSession', { params: { sessionId } }),
  });

  if (query.error !== null) {
    return (
      <ErrorState
        title="We couldn't load the transcript"
        {...retryIf(query.error, () => void query.refetch())}
        alternative={{ label: 'Back to the report', onClick: () => onNavigate('/me/reports') }}
      />
    );
  }

  if (query.isPending) {
    return (
      <main id="main" className="vl-page">
        <Skeleton lines={6} label="Loading the transcript" />
      </main>
    );
  }

  const turns = query.data?.turns ?? [];

  return (
    <main id="main" className="vl-page vl-transcript">
      <h1>Transcript</h1>
      {turns.length === 0 ? (
        <EmptyState
          title="Nothing was said in this one"
          body="The session ended before a turn was recorded."
          action={{ label: 'All reports', onClick: () => onNavigate('/me/reports') }}
        />
      ) : (
        <ol className="vl-transcript__list">
          {turns.map((turn) => (
            <li key={turn.index} className="vl-transcript__turn">
              <p className="vl-transcript__tutor" lang="es">
                {turn.tutorText}
              </p>
              {turn.tutorTranslation !== null && (
                <p className="vl-transcript__gloss">{turn.tutorTranslation}</p>
              )}
              {turn.learnerTranscript !== null && (
                <p className="vl-transcript__learner">
                  {/* Labelled as what we heard rather than as what they said: the
                      difference is the whole point of showing it. */}
                  <span className="vl-transcript__label">We heard</span> {turn.learnerTranscript}
                </p>
              )}
              {turn.corrections.length > 0 && (
                <ul className="vl-transcript__corrections">
                  {turn.corrections.map((correction) => (
                    <li key={`${turn.index}-${correction.original}`}>
                      {correction.original} → {correction.corrected}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ol>
      )}
    </main>
  );
}
